import { useState, useCallback } from "react";
import { compressFilters, decompressFilters, migrateFilterFormat } from "@/lib/client/filters";
import { useAuth } from "@clerk/nextjs";
import { sendError } from "@/lib/client/sentry";
import { useFiltersStore } from "@/stores/filtersStore";
const API_URL = process.env.NEXT_PUBLIC_API_URL || "";

type Props = {
  refreshCache?: boolean;
};

/**
 * Hook for managing user's saved search filters.
 *
 * Provides CRUD operations for saved filters. Filters are compressed
 * before storage and decompressed/migrated on retrieval.
 *
 * @returns Object containing:
 * - loadSavedFilters: Fetch user's saved filters
 * - saveNewFilter: Create a new saved filter
 * - updateSavedFilter: Update an existing saved filter
 * - deleteSavedFilter: Delete a saved filter
 * - updateFilterInterval: Set filter refresh interval
 * - toggleDailyEmail: Toggle daily email notifications
 * - savedFilters: Array of user's saved filters
 * - loading: Boolean indicating if data is being fetched
 *
 * @example
 * ```tsx
 * function SavedFiltersPanel() {
 *   const { savedFilters, loading, saveNewFilter, deleteSavedFilter } =
 *     useSavedFilters();
 *
 *   const handleSave = async (filter: SavedFilter) => {
 *     try {
 *       await saveNewFilter(filter);
 *       toast.success("Filter saved!");
 *     } catch (err) {
 *       toast.error("Failed to save filter");
 *     }
 *   };
 *
 *   if (loading) return <Spinner />;
 *
 *   return (
 *     <div>
 *       {savedFilters.map(filter => (
 *         <SavedFilterCard
 *           key={filter.id}
 *           filter={filter}
 *           onDelete={() => deleteSavedFilter(filter.id)}
 *         />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */

// Example mock data structure for reference (not used in production):
// const mockResult = {
// 	"filters": [
// 		{
// 			"id": 115,
// 			"itemtimestamp": "2025-05-01T03:25:53.201Z",
// 			"fkuserpreselect": "user_2u6VEs0DvqQ2HKTgmHiqkzdiiDK",
// 			"search_filter": {
// 				"k": 1746069952557,
// 				"name": "Raf",
// 				"searchText": "",
// 				"a": {
// 					"b": [],
// 					"c": [],
// 					"d": [
// 						{
// 							"o": 137,
// 							"n": "Raf Simons",
// 							"m": true,
// 							"p": "include"
// 						}
// 					],
// 					"e": [],
// 					"f": [],
// 					"g": [],
// 					"h": [],
// 					"i": [
// 						{
// 							"n": "AUD",
// 							"o": "AUD",
// 							"m": true,
// 							"p": "include"
// 						}
// 					],
// 					"j": [
// 						{
// 							"m": true,
// 							"n": "Newest",
// 							"o": "newest"
// 						}
// 					],
// 					"k": 1746069952557
// 				}
// 			},
// 			"interval": 120,
// 			"last_run": "2025-05-21T06:01:57.746Z",
// 			"email_active": true,
// 			"notification_active": true,
// 			"next_run": null
// 		},
// 		{
// 			"id": 65,
// 			"itemtimestamp": "2025-03-20T02:59:07.242Z",
// 			"fkuserpreselect": "user_2u6VEs0DvqQ2HKTgmHiqkzdiiDK",
// 			"search_filter": {
// 				"k": 1742439546278,
// 				"name": "Comme",
// 				"searchText": "",
// 				"a": {
// 					"b": [
// 						{
// 							"m": true,
// 							"n": "Available",
// 							"o": 1
// 						}
// 					],
// 					"c": [],
// 					"d": [
// 						{
// 							"o": 41,
// 							"n": "COMME des GARÇONS",
// 							"m": true,
// 							"p": "include"
// 						}
// 					],
// 					"e": [],
// 					"f": [],
// 					"g": [],
// 					"h": [],
// 					"i": [
// 						{
// 							"n": "AUD",
// 							"o": "AUD",
// 							"m": true,
// 							"p": "include"
// 						}
// 					],
// 					"j": [
// 						{
// 							"n": "Newest",
// 							"o": "newest",
// 							"m": true,
// 							"p": "include"
// 						}
// 					],
// 					"k": 1742439546278
// 				}
// 			},
// 			"interval": 0,
// 			"last_run": "2025-05-01T02:47:33.894Z",
// 			"email_active": false,
// 			"notification_active": false,
// 			"next_run": "2025-04-27T02:00:00.000Z"
// 		}
// 	]
// }

const useSavedFilters = () => {
  const [savedFilters, setSavedFilters] = useState<DBSavedFilter[]>([]);
  const [loading, setLoading] = useState(true);
  const { getToken, isSignedIn } = useAuth();

  const syncSavedFilters = useCallback((nextFilters: DBSavedFilter[]) => {
    setSavedFilters(nextFilters);
    useFiltersStore.getState().actions.setSavedFilters(nextFilters);
  }, []);

  const loadSavedFilters = useCallback(
    async ({ refreshCache = false }: Props = {}) => {
      if (!isSignedIn) {
        return;
      }
      try {
        const token = await getToken();
        if (!token) {
          setLoading(false);
          return;
        }
        const response = await fetch(`${API_URL}/api/v2/user/filters`, {
          credentials: "include",
          method: "GET",
          mode: "cors",
          headers: {
            "Content-Type": "application/json",
            "Cache-Control": refreshCache
              ? "no-store"
              : "private, max-age=86400, stale-while-revalidate=120",
            Authorization: `Bearer ${token}`,
          },
          cache: refreshCache ? "no-cache" : "default",
        });

        if (!response.ok) {
          sendError(new Error("Network response was not ok"), { status: response.status });
          setLoading(false);
          return;
        }

        const { data } = await response.json();
        // const result = mockResult;
        const decompressed = data.filters.map((filter: DBSavedFilter) => {
          const decompressedFilter = decompressFilters(filter.search_filter);
          const migratedFilter = migrateFilterFormat(decompressedFilter);
          return {
            ...filter,
            search_filter: migratedFilter,
          };
        });

        syncSavedFilters(decompressed);

        setLoading(false);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : "Unknown error";
        console.warn("Error fetching data:", message);
        sendError(err as Error, {});
        setLoading(false);
      }
    },
    [getToken, isSignedIn, syncSavedFilters]
  );

  const saveNewFilter = useCallback(
    async (filter: SavedFilter) => {
      try {
        const token = await getToken();
        if (!token) {
          return;
        }
        const compressedFilters = compressFilters(filter);
        const response = await fetch(`${API_URL}/api/v2/user/filters`, {
          credentials: "include",
          method: "POST",
          mode: "cors",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(compressedFilters),
        });

        // Check response and throw error with backend message
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to save filter");
        }

        await loadSavedFilters({ refreshCache: true });
      } catch (error) {
        console.warn("Error saving filter:", error);
        sendError(error as Error, { compressedFilters: JSON.stringify(filter) });
        throw error; // Re-throw so the UI can catch it
      }
    },
    [getToken, loadSavedFilters]
  );

  const updateSavedFilter = useCallback(
    async (filterId: number, filters: SavedFilter) => {
      try {
        const token = await getToken();
        if (!token) {
          return;
        }
        const compressedFilters = compressFilters(filters);
        const response = await fetch(`${API_URL}/api/v2/user/filters/${filterId}`, {
          credentials: "include",
          method: "PUT",
          mode: "cors",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ filters: compressedFilters }),
        });

        if (!response.ok) {
          throw new Error("Failed to update filter");
        }

        await loadSavedFilters({ refreshCache: true });
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : "Unknown error";
        console.warn("Error saving filter:", message);
        sendError(err as Error, { filterId, compressedFilters: JSON.stringify(filters) });
      }
    },
    [getToken, loadSavedFilters]
  );

  // Renames a filter in place — reuses updateSavedFilter's existing PUT
  // (which replaces the whole search_filter), spreading the filter's
  // current criteria/searchText/etc. so only the name actually changes.
  // Unlike saveFiltersStore's update path, this doesn't touch the live
  // activeFilters store, so it's safe to call on a filter that isn't the
  // one currently selected/active on the search page.
  const renameSavedFilter = useCallback(
    async (filter: DBSavedFilter, newName: string) => {
      const trimmed = newName.trim();
      if (!trimmed) return;
      await updateSavedFilter(filter.id, {
        ...filter.search_filter,
        name: trimmed,
      } as SavedFilter);
    },
    [updateSavedFilter]
  );

  const toggleDailyEmail = useCallback(
    async (filterId: number, emailActive: boolean) => {
      const previousFilters = useFiltersStore.getState().savedFilters;
      const optimisticFilters = previousFilters.map((filter) =>
        filter.id === filterId ? { ...filter, email_active: emailActive } : filter
      );
      syncSavedFilters(optimisticFilters);

      try {
        const token = await getToken();
        if (!token) {
          syncSavedFilters(previousFilters);
          return;
        }
        const response = await fetch(`${API_URL}/api/v2/user/toggle-filter-activation-both`, {
          credentials: "include",
          method: "POST",
          mode: "cors",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            id: filterId,
            type: "email",
            toggle: emailActive,
          }),
        }).then((res) => res.json());

        if (!response?.success) {
          throw new Error("Something went wrong");
        }

        // Keep existing local order stable and only patch fields returned by the API if present.
        if (response?.data?.filter) {
          const updatedFilter = response.data.filter as Partial<DBSavedFilter> & { id?: number };
          const patchedFilters = optimisticFilters.map((filter) =>
            filter.id === filterId ? { ...filter, ...updatedFilter } : filter
          );
          syncSavedFilters(patchedFilters);
        }
        return true;
      } catch (err: unknown) {
        syncSavedFilters(previousFilters);
        const message = err instanceof Error ? err.message : "Unknown error";
        console.warn("Error updating filter interval:", message);
        sendError(err as Error, { filterId, emailActive, type: "email" });
        return false;
      }
    },
    [getToken, syncSavedFilters]
  );

  const updateFilterInterval = useCallback(
    async (id: number, interval: number) => {
      try {
        const token = await getToken();
        if (!token) {
          return;
        }
        const response = await fetch(`${API_URL}/api/v2/user/toggle-filter-activation`, {
          credentials: "include",
          method: "POST",
          mode: "cors",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            id,
            interval,
            toggle: interval === 0 ? false : true,
          }),
        });

        await loadSavedFilters({ refreshCache: true });

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        return true;
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : "Unknown error";
        console.warn("Error updating filter interval:", message);
        sendError(err as Error, { id, interval, toggle: interval === 0 ? false : true });
        return false;
      }
    },
    [getToken, loadSavedFilters]
  );

  const deleteSavedFilter = useCallback(
    async (id: number) => {
      try {
        const token = await getToken();
        if (!token) {
          return;
        }
        await fetch(`${API_URL}/api/v2/user/filters/${id}`, {
          credentials: "include",
          method: "DELETE",
          mode: "cors",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        await loadSavedFilters({ refreshCache: true });
      } catch (error) {
        console.warn("Error deleting filter:", error);
        sendError(error as Error, { id });
      }
    },
    [getToken, loadSavedFilters]
  );

  /**
   * Generates (or fetches the existing) public share link for a saved
   * filter. Idempotent on the backend — calling this again for a filter
   * that's already shared returns the same token rather than rotating it.
   *
   * @returns the share token, or null if the request failed
   */
  const shareFilter = useCallback(
    async (id: number): Promise<string | null> => {
      try {
        const token = await getToken();
        if (!token) {
          return null;
        }
        const response = await fetch(`${API_URL}/api/v2/user/filters/${id}/share`, {
          credentials: "include",
          method: "POST",
          mode: "cors",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }).then((res) => res.json());

        return response?.success ? (response.data?.shareToken ?? null) : null;
      } catch (error) {
        console.warn("Error sharing filter:", error);
        sendError(error as Error, { id });
        return null;
      }
    },
    [getToken]
  );

  return {
    loadSavedFilters,
    saveNewFilter,
    updateSavedFilter,
    renameSavedFilter,
    deleteSavedFilter,
    shareFilter,
    savedFilters,
    loading,
    updateFilterInterval,
    toggleDailyEmail,
  };
};

export default useSavedFilters;
